import { ScrEnvironment } from './../../environments/environment';
import { Injectable } from '@angular/core';
import { of, Observable, Subject, PartialObserver, Subscription, BehaviorSubject } from 'rxjs';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { map, share, mergeMap, filter, first } from 'rxjs/operators';
import { ScrBaseResponse, ScrBaseService, ScrMessage } from './base.service';
import { ScrServiceManager } from './servicemanager.service';
import { ShortCodesRole, ShortCodesRoleLabels } from '../models/constants';
import { SafeHtml } from '@angular/platform-browser';


export interface Aggregator {
  aggregatorId: number;
  active: boolean;
  createTime: string;
  updateTime: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  name: string;
}

export interface SuspiciousActivityConfig {
  activityTypes: string[];
  activityStatuses: string[];
}

export class ConfigurationResponse extends ScrBaseResponse {
  constructor(
    status: string,
    public config: Config,
    public emailcategories: EmailCategory[],
    public countries: Country[],
    public aggregators: Aggregator[],
    public banners: BannerConfig[],
    public usregions: Region[],
    messages: ScrMessage[]) {
    super(status, messages);
  }
}

export interface LicenseAgreementConfig {
  updated: string;
  version: string;
  content: string;
}

export interface BestPracticesConfig {
  updated: string;
  version: string;
  content: string;
}

export interface InvoiceConfig {
   footer: string[];
   paymentInfo: string[];
   remittanceAddress: string[];
   overnightMailCarrierPayments: string[];
   electronicPaymentsVia: string[];
}
export interface SupplementalBusyInfo {
  text: string;
  waitTime: number;
}

export interface BannerConfig {
  content: string[];
  backgroundcolor: string;
  fontcolor: string;
  startDate?: string;
  endDate?: string;
  componentList: string[];

  // client side
  safe: SafeHtml;
}


export interface VettingConfig {
  cpVettingRequired: boolean;
}

/**
 * A type-safe server side configuration based on database entries.
 * Could make this an any type but that is a tradeoff.  Trying
 * a specific type to see how much of a pain it is...
 */
export interface Config {
  customerSupportEmail: string;
  customerSupportPhone: string;
  customerSupportLink: string;
  productName: string;
  launchUrl: string;
  senderEmailAddress: string;
  faqUrl: string;
  recaptchaEnabled: boolean;
  licenseAgreement: LicenseAgreementConfig;
  bestPractices: BestPracticesConfig;
  invoiceConfig: InvoiceConfig;
  retentionPeriodMinutes: number;
  maxCartItems: number;
  supplementalBusyInfo?: SupplementalBusyInfo;
  achIdentificationCode: string;
  achMaxPaymentLimit: number;
  suspiciousActivity: SuspiciousActivityConfig;
  legalEntityTypes: string[];
  ecosystemRoles: string[];
  brandsEnabled: boolean;
  publicEmailDomains: string[];
  excludedPublicEmailDomains: string[];

  vetting: VettingConfig;
}


export interface Country {
  country: string;
  countryName: string;
  regionRequired: boolean | undefined;
}

export interface Region {
  region: string;
  regionName: string;
}

export interface UserRole {
  label: string;
  value: string;
  disabled?: boolean;
}

export class EmailCategory {
  constructor(public emailCategoryId: number, public name: string, public description: string) { }
}


export class DependantFieldEvent<T> {
  constructor(public eventName: string,
    public source: string,
    public target: string,
    public value?: T) {
  }
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService extends ScrBaseService {

  private static WhenConsideredStale = 1000*60*60; // 1 hr.

  private lastUpdate = 0;
  private countries?: Country[];
  private regions: Map<string, Region[]>;
  private emailCategories: EmailCategory[];
  private aggregators: Aggregator[];
  private banners: BannerConfig[];


  private subject: Subject<DependantFieldEvent<Country>>;
  private insecureHttpClient: HttpClient;
  private configObs?: Observable<ConfigurationResponse>;
  private response?: ConfigurationResponse;

  public banners$  = new BehaviorSubject<BannerConfig[]>([]);

  constructor(
    serviceManager: ScrServiceManager,
    private environment: ScrEnvironment,
    handler: HttpBackend,
    private http: HttpClient) {
    super(serviceManager);

    this.subject = new Subject();
    this.regions = new Map();
    this.emailCategories = [];
    this.aggregators = [];
    this.banners = [];

    // Bypass http interceptor that requires a JWT
    this.insecureHttpClient = new HttpClient(handler);
  }


  getConfig(): Observable<Config> {
    return this.cachedGet().pipe(map(r => r.config));
  }

  cachedGet(): Observable<ConfigurationResponse> {
    if (this.response) {
      return of(this.response);
    }
    else if (this.configObs) {
      return this.configObs;
    }
    else {
      return this.doGet();
    }
  }

  doGet(): Observable<ConfigurationResponse> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('x-api-key', this.environment.api.scr.apikey);
    const url = `${this.environment.base}/public/config`;
    this.configObs = this.insecureHttpClient.get<ConfigurationResponse>(url, { headers }).pipe(map(res => {
      this.lastUpdate = new Date().getTime();
      this.response = res;
      this.emailCategories = res.emailcategories;
      this.aggregators = res.aggregators;
      this.banners = res.banners;
      this.banners$.next(res.banners);
      if (res.usregions) {
        this.regions.set('us', res.usregions);
      }
      if (res.countries) {
        this.countries = res.countries;
        this.countries.forEach(c => {
          if (!c.regionRequired) {
            c.regionRequired = (c.country === 'us') || (c.country === 'canada');
          }
        });
      }
      return res;
    }), share());
    return this.configObs;
  }

  getCountries(reload = false): Observable<Country[]> {
    if (this.countries && reload === false) {
      return of(this.countries);
    }
    return this.cachedGet().pipe(map(r => r.countries));
  }

  getBanners(reload = false): Observable<BannerConfig[]> {
    if (this.banners && reload === false) {
      return of(this.banners);
    }
    return this.cachedGet().pipe(map(r => r.banners));
  }

  getRegionsByCountryName(countryname: string, reload = false): Observable<Region[] | undefined> {
    return this.getCountries().pipe(mergeMap(countries => {
      const match = countries.find(c => c.countryName === countryname);
      if (match) {
        return this.getRegions(match.country, reload);
      }
      else {
        console.error('no match for country named ' + countryname);
        return of([]);
      }
    }));
  }

  getRegions(country: string, reload = false): Observable<Region[] | undefined> {
    if (this.regions.has(country) && reload === false) {
      return of(this.regions.get(country));
    }
    const url = `${this.environment.base}/public/regions/countries/$country/subregions`;
    const e = encodeURIComponent(country);
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('x-api-key', this.environment.api.scr.apikey);
    return this.nobusy<Region[] | undefined>(() =>
      this.http.get<{ regions: Region[] }>(url.replace('$country', e), { headers }).pipe(map(res => {
        this.regions.set(country, res.regions);
        return this.regions.get(country);
      }))
    );
  }

  getAggregators(): Observable<Aggregator[]> {
    if (this.aggregators.length > 0) {
      return of(this.aggregators);
    }
    return this.cachedGet().pipe(map(r => r.aggregators));
  }

  getSuspiciousActivityConfig(): Observable<SuspiciousActivityConfig> {
    return this.cachedGet().pipe(map(r => r.config.suspiciousActivity));
  }

  getEmailCategories(): Observable<EmailCategory[]> {
    if (this.emailCategories.length > 0) {
      return of(this.emailCategories);
    }
    return this.cachedGet().pipe(map(r => r.emailcategories));
  }


  getRoles(): UserRole[] {
    return [
      { label: ShortCodesRoleLabels.Registrant, value: ShortCodesRole.Registrant, disabled: false },
      { label: ShortCodesRoleLabels.RegistrantAdmin, value: ShortCodesRole.RegistrantAdmin, disabled: false }
    ];
  }

  getCarrierRoles(): UserRole[] {
    return [
      { label: ShortCodesRoleLabels.CarrierRegistrant, value: ShortCodesRole.CarrierRegistrant, disabled: false },
      { label: ShortCodesRoleLabels.Carrier, value: ShortCodesRole.Carrier, disabled: false }
    ];
  }

  getSdRoles(): UserRole[] {
    return [
      { label: ShortCodesRoleLabels.API, value: ShortCodesRole.API, disabled: false },
      { label: ShortCodesRoleLabels.BIReports, value: ShortCodesRole.BIReports, disabled: false },
      { label: ShortCodesRoleLabels.Carrier, value: ShortCodesRole.Carrier, disabled: false },
      { label: ShortCodesRoleLabels.CarrierRegistrant, value: ShortCodesRole.CarrierRegistrant, disabled: false },
      { label: ShortCodesRoleLabels.CTIAAdmin, value: ShortCodesRole.CTIAAdmin, disabled: false },
      { label: ShortCodesRoleLabels.PublicAPI, value: ShortCodesRole.PublicAPI, disabled: false },
      { label: ShortCodesRoleLabels.Registrant, value: ShortCodesRole.Registrant, disabled: false },
      { label: ShortCodesRoleLabels.RegistrantAdmin, value: ShortCodesRole.RegistrantAdmin, disabled: false },
      { label: ShortCodesRoleLabels.ServiceDesk, value: ShortCodesRole.ServiceDesk, disabled: false },
      { label: ShortCodesRoleLabels.ServiceDeskOperations, value: ShortCodesRole.ServiceDeskOperations, disabled: false },
      { label: ShortCodesRoleLabels.SuperAdmin, value: ShortCodesRole.SuperAdmin, disabled: false },
      { label: ShortCodesRoleLabels.VettingMonitoring, value: ShortCodesRole.VettingMonitoring, disabled: false },
      { label: ShortCodesRoleLabels.VettingMonitoringAPI, value: ShortCodesRole.VettingMonitoringAPI, disabled: false },
    ];
  }

  /**
   * Allow dependant fields to communicate via this service.
   * @param event - event to be dispatched.
   */
  emit(event: DependantFieldEvent<Country>): void {
    this.subject.next(event);
  }

  /**
   * Allow dependant fields to communicate via this service.
   * @param name - event name to listen for
   * @param observer - observer to notify.
   */
  on(name: string, observer: PartialObserver<DependantFieldEvent<Country>>): Subscription {
    return this.subject
      .pipe(
        filter((e: DependantFieldEvent<Country>) => {
          return e.eventName === name;
        })
      )
      .subscribe(observer);
  }

  /**
   * SD can update banners.  This updates the running app so SD user can see.
   * @param banners - banners
   */
  updateBanners(): void {
    this.doGet().pipe(first()).subscribe(() => {
      // do nothing
    });
  }

  /**
   * Maybe update the config in the background.
   */
  checkForUpdate(): void {
    const now = new Date().getTime();
    if ((!this.lastUpdate) || ((now - this.lastUpdate) > ConfigService.WhenConsideredStale)) {
        this.doGet().pipe(first()).subscribe(() => { 
          // do nothing
        });
      }
  }
}
